import { AxiosResponse } from 'axios';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { createUrl, request, stripTags } from 'app/utils';
import { Loading, NotFound, Page } from 'components';
import { AgreementsMessage } from 'features/agreements';
import {
  ContentArticleView,
  ContentDetailsApiResponse,
  ContentPdfView,
  ContentQuizView,
  ContentScormView,
  ContentSurveyView,
  ContentVideoView,
  ContentZoomView,
  SendFeedbackProps,
  useContents,
} from 'features/contents';
import { ProductEvolutionDetailsApiResponse, useProducts } from 'features/products';
import { useSchool } from 'features/school';
// import { ModulesDetailsApiResponse, useModules } from 'features/modules';

interface ContentsDetailsProps {
  isLive?: boolean;
  noProduct?: boolean;
  isScorm?: boolean;
}

const ContentsDetailsView = ({ isLive, isScorm, noProduct = false }: ContentsDetailsProps): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { productSlug, productClassUuid, contentTypeSlug, contentUuid } = useParams();
  const { apiGatewayUrl } = useSchool();
  const { contentDetails, setContentDetails, clearContentDetails } = useContents();
  const { setProductEvolutionDetails, clearProductEvolutionDetails } = useProducts();
  // const { setModuleDetails, clearModuleDetails } = useModules();

  const fetchContent = useCallback(async () => {
    setLoading(true);

    await Promise.all(
      isScorm
        ? [
            request({
              url: `/api/v1/School/ProductContentItem/null/${contentUuid}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            })
              .then((response: AxiosResponse<ContentDetailsApiResponse>) => {
                setContentDetails(response.data);
              })
              .catch(() => {
                setNotFound(true);
              }),
          ]
        : isLive && noProduct
        ? [
            request({
              url: `/api/v1/School/ProductContentItem/${productSlug ? productSlug : 'null'}/${contentUuid}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            })
              .then((response: AxiosResponse<ContentDetailsApiResponse>) => {
                setContentDetails(response.data);
              })
              .catch(() => {
                setNotFound(true);
              }),
          ]
        : [
            // request({
            //   url: `/api/v1/School/ProductContentTimeline/${productSlug}/${moduleUuid}`,
            //   method: 'get',
            //   baseURL: apiGatewayUrl,
            // }).then((response: AxiosResponse<ModulesDetailsApiResponse>) => {
            //   setModuleDetails(response.data);
            // }),
            request({
              url: `/api/v1/School/ProductContentItem/${productSlug}/${contentUuid}${
                productClassUuid ? `?productClassUuid=${productClassUuid}` : ``
              }`,
              method: 'get',
              baseURL: apiGatewayUrl,
            })
              .then((response: AxiosResponse<ContentDetailsApiResponse>) => {
                setContentDetails(response.data);
              })
              .catch(() => {
                setNotFound(true);
              }),

            request<ProductEvolutionDetailsApiResponse>({
              url: `/api/v1/School/ProductAssessments/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            }).then((response) => {
              setProductEvolutionDetails(response.data);
            }),
          ]
    ).then(() => {
      setLoading(false);
    });
  }, [contentUuid, isScorm, isLive, noProduct, apiGatewayUrl, productSlug, contentTypeSlug, productClassUuid]);

  const sendFeedback = useCallback(
    ({ liked, feedbackText = '' }: SendFeedbackProps) => {
      request({
        url: `/api/v1/School/AssessContentFeedback`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          contentUuid: contentDetails && contentDetails.contentUuid,
          productClassUuid: contentDetails && contentDetails.productClassUuid,
          liked,
          feedbackText,
        },
      });
    },
    [contentDetails]
  );

  useEffect(() => {
    if (contentDetails) {
      if (contentTypeSlug === 'video') {
        if (contentDetails.video?.isLiveMeeting && !isLive) {
          window.location.href = createUrl('liveDetails', [productSlug, contentDetails.contentUuid]);
        }
      }
    }
  }, [productSlug, contentDetails, contentTypeSlug]);

  useEffect(() => {
    fetchContent();

    return () => {
      clearContentDetails();
      clearProductEvolutionDetails();
      // clearModuleDetails();
    };
  }, [contentUuid, isScorm, isLive, noProduct, apiGatewayUrl, productSlug, productClassUuid, contentTypeSlug]);

  if (notFound) return <NotFound />;

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={stripTags(contentDetails && contentDetails.name)}>
      <AgreementsMessage productSlug={productSlug} />

      {productSlug === 'gerindo-os-leads-e-o-nosso-processo-comercial' ? (
        <ContentScormView />
      ) : (
        <>
          {contentTypeSlug === 'article' && <ContentArticleView />}
          {contentTypeSlug === 'quiz' && <ContentQuizView />}
          {contentTypeSlug === 'video' && <ContentVideoView sendFeedback={sendFeedback} />}
          {contentTypeSlug === 'zoom' && <ContentZoomView />}
          {contentTypeSlug === 'survey' && <ContentSurveyView />}
          {contentTypeSlug === 'pdf' && <ContentPdfView sendFeedback={sendFeedback} />}
        </>
      )}
    </Page>
  );
};
export default ContentsDetailsView;
